"use client";

import {
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { HINT } from "@/components/tool-kit/styles";
import { QuantityField } from "@/components/tools/physics-mechanics/quantity-field";
import { Working } from "@/components/tools/physics-mechanics/working";
import { t } from "@/lib/i18n/interpolate";
import {
  formatPlain,
  formatSig,
  type NumberFormatter,
  withUnit,
} from "@/lib/tool-kit/physics-mechanics/format";
import {
  type CollisionError,
  type CollisionInput,
  type CollisionResult,
  type CollisionType,
  solveCollision,
} from "@/lib/tool-kit/physics-mechanics/momentum";
import { parseQuantity } from "@/lib/tool-kit/physics-mechanics/quantity";
import {
  fromBase,
  MASS,
  SPEED,
  toBase,
  unitOptions,
  unitSymbol,
} from "@/lib/tool-kit/physics-mechanics/units";

export interface CollisionField {
  value: string;
  unit: string;
}

export interface CollisionState {
  type: CollisionType;
  restitution: string;
  m1: CollisionField;
  u1: CollisionField;
  m2: CollisionField;
  u2: CollisionField;
}

export interface CollisionStrings {
  collisionType: string;
  typeElastic: string;
  typeInelastic: string;
  typePartial: string;
  restitution: string;
  restitutionHint: string;
  mass1: string;
  speed1: string;
  mass2: string;
  speed2: string;
  unitFor: string;
  collisionPositive: string;
  after1: string;
  after2: string;
  momentumTotal: string;
  keBefore: string;
  keAfter: string;
  keLost: string;
  workingHeading: string;
  stepMomentum: string;
  stepElastic: string;
  stepInelastic: string;
  stepPartial: string;
  stepEnergy: string;
  copy: string;
  copied: string;
  copyText: string;
  collisionNeedAll: string;
  collisionUnreadable: string;
  collisionErrors: Record<CollisionError, string>;
}

const FIELDS = ["m1", "u1", "m2", "u2"] as const;
type FieldKey = (typeof FIELDS)[number];
const FAMILY = { m1: MASS, u1: SPEED, m2: MASS, u2: SPEED } as const;

export function CollisionInputs({
  state,
  onState,
  strings,
}: {
  state: CollisionState;
  onState: (state: CollisionState) => void;
  strings: CollisionStrings;
}) {
  const labels: Record<FieldKey, string> = {
    m1: strings.mass1,
    u1: strings.speed1,
    m2: strings.mass2,
    u2: strings.speed2,
  };
  return (
    <>
      <PillGroup
        label={strings.collisionType}
        name="mo-collision-type"
        onChange={(type) => onState({ ...state, type })}
        options={[
          { value: "elastic", label: strings.typeElastic },
          { value: "inelastic", label: strings.typeInelastic },
          { value: "partial", label: strings.typePartial },
        ]}
        value={state.type}
      />
      <p className={HINT}>{strings.collisionPositive}</p>
      <div className="grid gap-4 sm:grid-cols-2">
        {FIELDS.map((key) => (
          <QuantityField
            id={`mo-col-${key}`}
            key={key}
            label={labels[key]}
            onUnit={(unit) =>
              onState({ ...state, [key]: { ...state[key], unit } })
            }
            onValue={(value) =>
              onState({ ...state, [key]: { ...state[key], value } })
            }
            unit={state[key].unit}
            unitLabel={t(strings.unitFor, { name: labels[key] })}
            units={unitOptions(FAMILY[key])}
            value={state[key].value}
          />
        ))}
        {state.type === "partial" ? (
          <QuantityField
            hint={strings.restitutionHint}
            id="mo-col-e"
            label={strings.restitution}
            onUnit={() => undefined}
            onValue={(restitution) => onState({ ...state, restitution })}
            unit="1"
            unitLabel={t(strings.unitFor, { name: strings.restitution })}
            units={[{ value: "1", label: "" }]}
            value={state.restitution}
          />
        ) : null}
      </div>
    </>
  );
}

type Read =
  | { kind: "ok"; input: CollisionInput }
  | { kind: "incomplete" }
  | { kind: "unreadable"; key: FieldKey | "e" };

function readCollision(state: CollisionState): Read {
  const values = {} as Record<FieldKey, number>;
  for (const key of FIELDS) {
    if (state[key].value.trim() === "") {
      return { kind: "incomplete" };
    }
    const parsed = parseQuantity(state[key].value);
    if (parsed === null) {
      return { kind: "unreadable", key };
    }
    values[key] = toBase(parsed, FAMILY[key], state[key].unit);
  }
  let restitution = 1;
  if (state.type === "partial") {
    const parsed = parseQuantity(state.restitution);
    if (parsed === null) {
      return state.restitution.trim() === ""
        ? { kind: "incomplete" }
        : { kind: "unreadable", key: "e" };
    }
    restitution = parsed;
  }
  return { kind: "ok", input: { ...values, type: state.type, restitution } };
}

function collisionSteps(
  input: CollisionInput,
  result: CollisionResult,
  strings: CollisionStrings,
  fmt: NumberFormatter,
  sig: number
): string[] {
  const p = (value: number) => formatPlain(fmt, value);
  const r = (value: number) => formatSig(fmt, value, sig);
  const total = input.m1 + input.m2;
  const steps = [
    t(strings.stepMomentum, {
      m1: p(input.m1),
      u1: p(input.u1),
      m2: p(input.m2),
      u2: p(input.u2),
      p: r(result.momentumBefore),
    }),
  ];
  if (input.type === "inelastic") {
    steps.push(
      t(strings.stepInelastic, {
        p: r(result.momentumBefore),
        total: p(total),
        v: r(result.v1),
      })
    );
  } else if (input.type === "elastic") {
    steps.push(
      t(strings.stepElastic, {
        m1: p(input.m1),
        u1: p(input.u1),
        m2: p(input.m2),
        u2: p(input.u2),
        v1: r(result.v1),
        v2: r(result.v2),
      })
    );
  } else {
    steps.push(
      t(strings.stepPartial, {
        e: p(result.restitution),
        approach: p(input.u1 - input.u2),
        v1: r(result.v1),
        v2: r(result.v2),
      })
    );
  }
  steps.push(
    t(strings.stepEnergy, {
      before: r(result.keBefore),
      after: r(result.keAfter),
      lost: r(result.keLost),
      share: r(result.fractionLost * 100),
    })
  );
  return steps;
}

export function CollisionResults({
  state,
  strings,
  fmt,
  sig,
}: {
  state: CollisionState;
  strings: CollisionStrings;
  fmt: NumberFormatter;
  sig: number;
}) {
  const read = readCollision(state);
  if (read.kind === "incomplete") {
    return <Notice>{strings.collisionNeedAll}</Notice>;
  }
  if (read.kind === "unreadable") {
    const names = {
      m1: strings.mass1,
      u1: strings.speed1,
      m2: strings.mass2,
      u2: strings.speed2,
      e: strings.restitution,
    };
    return (
      <ErrorNotice>
        {t(strings.collisionUnreadable, { name: names[read.key] })}
      </ErrorNotice>
    );
  }
  const result = solveCollision(read.input);
  if ("error" in result) {
    return <ErrorNotice>{strings.collisionErrors[result.error]}</ErrorNotice>;
  }
  const unit = state.u1.unit;
  const speed = (si: number) =>
    withUnit(
      formatSig(fmt, fromBase(si, SPEED, unit), sig),
      unitSymbol(SPEED, unit)
    );
  const joules = (value: number) => withUnit(formatSig(fmt, value, sig), "J");
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.after1} value={speed(result.v1)} />
        <StatCard emphasis label={strings.after2} value={speed(result.v2)} />
        <StatCard
          label={strings.momentumTotal}
          value={withUnit(formatSig(fmt, result.momentumAfter, sig), "kg·m/s")}
        />
        <StatCard label={strings.keBefore} value={joules(result.keBefore)} />
        <StatCard label={strings.keAfter} value={joules(result.keAfter)} />
        <StatCard
          label={strings.keLost}
          value={`${joules(result.keLost)} (${withUnit(formatSig(fmt, result.fractionLost * 100, sig), "%")})`}
        />
      </StatGrid>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        small
        text={t(strings.copyText, {
          v1: speed(result.v1),
          v2: speed(result.v2),
        })}
      />
      <Working
        heading={strings.workingHeading}
        steps={collisionSteps(read.input, result, strings, fmt, sig)}
      />
    </>
  );
}
